export type ConsentCategory = "essential" | "analytics" | "advertising";

export type ConsentState = {
  essential: true;
  analytics: boolean;
  advertising: boolean;
  updatedAt: string;
  version: number;
};

export type ConsentListener = (state: ConsentState) => void;

const STORAGE_KEY = "ror.consent.v1";
const CONSENT_VERSION = 1;

const DEFAULT_STATE: ConsentState = {
  essential: true,
  analytics: false,
  advertising: false,
  updatedAt: "",
  version: CONSENT_VERSION,
};

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeStorage(): StorageLike | null {
  try {
    const s = globalThis.localStorage;
    const probe = "__ror_probe__";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function parseStored(raw: string | null): ConsentState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return {
      essential: true,
      analytics: !!parsed.analytics,
      advertising: !!parsed.advertising,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export class ConsentStore {
  private state: ConsentState;
  private hasChoice: boolean;
  private listeners = new Set<ConsentListener>();
  private storage: StorageLike | null;
  private now: () => string;

  constructor(opts: { storage?: StorageLike | null; now?: () => string } = {}) {
    this.storage = opts.storage === undefined ? safeStorage() : opts.storage;
    this.now = opts.now ?? (() => new Date().toISOString());
    const stored = parseStored(this.storage?.getItem(STORAGE_KEY) ?? null);
    this.state = stored ?? { ...DEFAULT_STATE };
    this.hasChoice = stored !== null;
  }

  getState(): ConsentState {
    return { ...this.state };
  }

  hasMadeChoice(): boolean {
    return this.hasChoice;
  }

  isGranted(category: ConsentCategory): boolean {
    return this.state[category] === true;
  }

  acceptAll(): void {
    this.commit({ analytics: true, advertising: true });
  }

  rejectAll(): void {
    this.commit({ analytics: false, advertising: false });
  }

  set(partial: { analytics?: boolean; advertising?: boolean }): void {
    this.commit({
      analytics: partial.analytics ?? this.state.analytics,
      advertising: partial.advertising ?? this.state.advertising,
    });
  }

  clear(): void {
    this.storage?.removeItem(STORAGE_KEY);
    this.state = { ...DEFAULT_STATE };
    this.hasChoice = false;
    this.emit();
  }

  subscribe(listener: ConsentListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private commit(next: { analytics: boolean; advertising: boolean }): void {
    this.state = {
      essential: true,
      analytics: next.analytics,
      advertising: next.advertising,
      updatedAt: this.now(),
      version: CONSENT_VERSION,
    };
    this.hasChoice = true;
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Storage full or blocked — state still lives in memory for the session.
    }
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getState();
    for (const l of this.listeners) l(snapshot);
  }
}

export const CONSENT_STORAGE_KEY = STORAGE_KEY;
